/**
 * Voice agent API (vagent: sessions, offer; eventmanager: advisor tools).
 * Base: https://dev.vagent.circuitry.ai
 * Headers: Authorization Bearer {token}, x-id-token {token}
 */
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import { RootState } from '../../store';
import { getVoiceAgentBaseUrl, handleErrorResponse } from '../api';
import type {
  BusinessToolDetail,
  BusinessToolsListResponse,
  BusinessToolTestRequest,
  BusinessToolTestResponse,
  CreateBusinessToolRequest,
  CreateBusinessToolResponse,
  OfferRequest,
  OfferResponse,
  UpdateBusinessToolRequest,
} from './voiceAgentTypes';

const voiceLogsPrepareHeaders = (headers: Headers, { getState }: { getState: () => unknown }) => {
  const token = (getState() as RootState)?.auth?.token;
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
    headers.set('x-id-token', token);
  }
  return headers;
};

const voiceLogsInnerBaseQuery = fetchBaseQuery({
  baseUrl: '/',
  prepareHeaders: voiceLogsPrepareHeaders,
});

const voiceAgentLogsDynamicBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const host = getVoiceAgentBaseUrl();
  if (!host) {
    return {
      error: {
        status: 'CUSTOM_ERROR',
        error: 'Voice agent base URL is not configured. Call setVoiceAgentConfig({ baseUrl }) from your app entry with CTY_VOICE_AGENT_BASE_URL.',
      } as FetchBaseQueryError,
    };
  }
  const path = typeof args === 'string' ? args : args.url;
  const url = path.startsWith('http') ? path : `${host}${path.startsWith('/') ? path : `/${path}`}`;
  const adjusted = typeof args === 'string' ? url : { ...args, url };
  return voiceLogsInnerBaseQuery(adjusted, api, extraOptions);
};

export const voiceAgentLogsApi = createApi({
  reducerPath: 'voiceAgentLogsApi',
  tagTypes: ['AgentAdvisorTools', 'BusinessTools'],
  baseQuery: voiceAgentLogsDynamicBaseQuery,
  endpoints: (builder) => ({
    postOffer: builder.mutation<OfferResponse, OfferRequest>({
      query: (body) => ({
        url: 'vagent/api/offer',
        method: 'POST',
        body,
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    getAgentAdvisorTools: builder.query<{ agent_id: string; advisor_ids: string[] }, string>({
      query: (agent_id) => `eventmanager/api/circuitry/agent_advisor_tools?agent_id=${encodeURIComponent(agent_id)}`,
      transformErrorResponse: handleErrorResponse,
      providesTags: (_result, _error, agentId) => [{ type: 'AgentAdvisorTools', id: agentId }],
    }),
    postAdvisorTool: builder.mutation<
      { status: string; message: string },
      { agent_id: string; advisor_id: string; name: string; description: string; enabled: boolean }
    >({
      query: (body) => ({
        url: 'eventmanager/api/circuitry/advisor_tool',
        method: 'POST',
        body,
      }),
      transformErrorResponse: handleErrorResponse,
      invalidatesTags: (_result, _error, arg) => [{ type: 'AgentAdvisorTools', id: arg.agent_id }],
    }),
    getVoiceAgentFromAdvisor: builder.query<{ agents: { id: string; name: string }[] }, string>({
      query: (advisor_id) => `eventmanager/api/circuitry/advisor_agents?advisor_id=${advisor_id}`,
      transformErrorResponse: handleErrorResponse,
    }),
    getBusinessTools: builder.query<BusinessToolsListResponse, void>({
      query: () => 'vagent/api/business-tools',
      providesTags: [{ type: 'BusinessTools', id: 'LIST' }],
      transformErrorResponse: handleErrorResponse,
    }),
    getBusinessToolById: builder.query<BusinessToolDetail, string>({
      query: (toolId) => `vagent/api/business-tools/${encodeURIComponent(toolId)}`,
      providesTags: (_result, _error, toolId) => [{ type: 'BusinessTools', id: toolId }],
      transformErrorResponse: handleErrorResponse,
    }),
    createBusinessTool: builder.mutation<CreateBusinessToolResponse, CreateBusinessToolRequest>({
      query: (body) => ({
        url: 'vagent/api/business-tools',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'BusinessTools', id: 'LIST' }],
      transformErrorResponse: handleErrorResponse,
    }),
    updateBusinessTool: builder.mutation<
      BusinessToolDetail,
      { toolId: string; body: UpdateBusinessToolRequest }
    >({
      query: ({ toolId, body }) => ({
        url: `vagent/api/business-tools/${encodeURIComponent(toolId)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { toolId }) => [
        { type: 'BusinessTools', id: 'LIST' },
        { type: 'BusinessTools', id: toolId },
      ],
      transformErrorResponse: handleErrorResponse,
    }),
    deleteBusinessTool: builder.mutation<{ success?: boolean; message?: string }, string>({
      query: (toolId) => ({
        url: `vagent/api/business-tools/${encodeURIComponent(toolId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'BusinessTools', id: 'LIST' }],
      transformErrorResponse: handleErrorResponse,
    }),
    testBusinessTool: builder.mutation<
      BusinessToolTestResponse,
      { toolId: string; body: BusinessToolTestRequest }
    >({
      query: ({ toolId, body }) => ({
        url: `vagent/api/business-tools/${encodeURIComponent(toolId)}/test`,
        method: 'POST',
        body,
      }),
      transformErrorResponse: handleErrorResponse,
    }),
  }),
});

export const {
  usePostOfferMutation,
  useGetAgentAdvisorToolsQuery,
  usePostAdvisorToolMutation,
  useGetVoiceAgentFromAdvisorQuery,
  useGetBusinessToolsQuery,
  useGetBusinessToolByIdQuery,
  useLazyGetBusinessToolByIdQuery,
  useCreateBusinessToolMutation,
  useUpdateBusinessToolMutation,
  useDeleteBusinessToolMutation,
  useTestBusinessToolMutation,
} = voiceAgentLogsApi;
